import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismMetricBlock } from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import type { Dayjs } from 'dayjs';
import { t } from 'ember-intl';
import GeneralPercentageChangeValue from 'lumen/components/general/percentage-change-value';
import type {
  Period,
  RestrictionRecommendation,
  RoomOccupancy,
} from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { getRoomtypeAvailabilitytooltipReasonTranslation } from 'lumen/utils/channel-pricing/get-roomtype-availability-tooltip-reason-translations';
import { RESTRICTION_MODEL_CONTEXTS } from 'lumen/utils/constants/channel-pricing-constants';

import styles from './day-cell-room-type-availability-tooltip.module.css';

interface HotelChannelPricingTooltipsDayCellRoomTypeAvailabilityTooltipSignature {
  Args: {
    date: Dayjs;
    period: Period;
  };
}

export default class HotelChannelPricingTooltipsDayCellRoomtypeAvailabilityTooltip extends Component<HotelChannelPricingTooltipsDayCellRoomTypeAvailabilityTooltipSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  get formattedDate(): string {
    return this.args.date.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
    );
  }

  get hotelLevelRestrictionRecommendation():
    | RestrictionRecommendation
    | undefined {
    return this.args.period.restrictionRecommendations?.find(
      (recommendation) =>
        recommendation.context === RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
    );
  }

  get restrictionRecommendationReasonTranslation(): string {
    const recommendation = this.hotelLevelRestrictionRecommendation;
    const type = recommendation?.formattedValues?.[0]?.type;
    const value = recommendation?.formattedValues?.[0]?.value;

    return getRoomtypeAvailabilitytooltipReasonTranslation(type, value) || '';
  }

  get roomAvailabilityTooltipData(): {
    displayValue: string | number;
    isBold: boolean;
    roomName: string;
  }[] {
    const hotel = this.hotelAndMarketService.hotel;
    const roomNameById =
      hotel?.pricingIntegration?.pms_id_to_pms_room_name ?? {};

    return this.roomOccupancies
      .map((roomOccupancy) => {
        let isBold = false;

        if (!roomOccupancy.isMissing && !roomOccupancy.isSoldOut) {
          isBold = true;
        }

        return {
          roomName: roomNameById[roomOccupancy.roomId] || roomOccupancy.roomId,
          displayValue: roomOccupancy.formattedRoomsAvailable,
          isBold,
        };
      })
      .sort((a, b) => a.roomName.localeCompare(b.roomName));
  }

  get roomOccupancies(): RoomOccupancy[] {
    return this.args.period.day.roomOccupancies ?? [];
  }

  get totalAvailability(): string | number {
    // If all roomOccupancies are missing, return the 'general.missing' translation
    if (
      this.roomOccupancies.length > 0 &&
      this.roomOccupancies.every((roomOccupancy) => roomOccupancy.isMissing)
    ) {
      return this.intl.t('general.missing');
    }
    return this.roomOccupancies.reduce((total, roomOccupancy) => {
      const available = roomOccupancy.roomsAvailable;
      // Only count valid positive values, exclude -10000 (missing) and 0 (sold out)
      if (available && available > 0) {
        return total + available;
      }
      return total;
    }, 0);
  }

  <template>
    <EmberTooltip
      {{!  Hardcoded side property, but currently the component is only used in Smart Distribution. Can be refactored later if needed. }}
      @side="right-start"
      @tooltipClass="base-tooltip {{styles.tooltip}}"
    >
      <div class="{{styles.header}} flex items-center">
        <h4>
          {{this.formattedDate}}
        </h4>
      </div>
      <div
        {{! Made border styles conditional because the content below is FF dependant }}
        class="border-neutral-default flex px-200
          {{if this.access.hasRoomTypeAvailability 'border-b border-solid' ''}}"
      >
        <PrismMetricBlock
          class="flex-1"
          data-test-id="OTB-forecast-metric-block"
        >
          <:value>
            <div class="flex items-center gap-50">
              <p>{{@period.day.occupancy.formattedOccupancyPercentage}}</p>
              {{#if @period.showOccupancyChange}}
                <GeneralPercentageChangeValue
                  @changeValue={{@period.occupancyChange}}
                />
              {{/if}}
            </div>
          </:value>
          <:label>
            {{t "rates.OTB"}}
            ({{t "general.daysago" count=1}})
          </:label>
        </PrismMetricBlock>
        {{#if this.access.hasOTBForecastView}}
          <PrismMetricBlock class="flex-1">
            <:value>
              <div class="flex items-center gap-50">
                <p
                >{{@period.day.predictedOccupancy.formattedOccupancyPercentage}}</p>
                {{#if @period.showPredictedOccupancyChange}}
                  <GeneralPercentageChangeValue
                    @changeValue={{@period.predictedOccupancyChange}}
                  />
                {{/if}}
              </div>
            </:value>
            <:label>
              {{t
                "channel_pricing.room_type_availability_tooltip.forecast_OTB"
              }}
            </:label>
          </PrismMetricBlock>
        {{/if}}
      </div>
      {{#if this.restrictionRecommendationReasonTranslation}}
        <div class="{{styles.reason-infoblock}} p-300">
          <p class="text-200--regular">
            {{t
              this.restrictionRecommendationReasonTranslation
              classNameBold="text-200--bold"
              htmlSafe=true
            }}
          </p>
        </div>
      {{/if}}
      {{#if this.access.hasRoomTypeAvailability}}
        <table
          class="{{styles.table}}
            border-neutral-default w-full border-b border-solid"
          data-test-id="room-type-availability-table"
        >
          <thead>
            <tr
              class="border-neutral-default text-200--medium flex items-center border-b border-solid"
            >
              <th class={{styles.room-type-column}}>
                {{t "pricing_assistant.label.room_type"}}
              </th>
              <th class={{styles.availability-column}}>
                {{t "general.availability"}}
              </th>
            </tr>
          </thead>
          <tbody>
            {{#each this.roomAvailabilityTooltipData as |room|}}
              <tr class="text-200--regular flex items-center">
                <td class="{{styles.room-type-column}} truncate">
                  {{room.roomName}}
                </td>
                <td
                  class="{{styles.availability-column}}
                    {{if room.isBold 'text-200--bold'}}"
                >
                  {{room.displayValue}}
                </td>
              </tr>
            {{/each}}
          </tbody>
        </table>
        <div class="text-200--bold flex items-center justify-between p-200">
          <p>{{t
              "channel_pricing.room_type_availability_tooltip.total_availability"
            }}</p>
          <p>{{this.totalAvailability}}</p>
        </div>
      {{/if}}
    </EmberTooltip>
  </template>
}
